import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Boxes, Check, Power, Timer } from "lucide-react";
import { get, put } from "@/lib/api";
import { fmtNum } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LoadingState, ErrorState } from "@/components/common/States";
import { cn } from "@/lib/utils";

const prix = (v) => (v == null ? "sur devis" : `${fmtNum(v)} FCFA`);
const S = "h-8 w-full bg-input border border-border rounded-sm px-2 text-sm";

const BADGE = {
  actif: "border-emerald-500/30 text-emerald-400 bg-emerald-500/10",
  essai: "border-amber-500/30 text-amber-400 bg-amber-500/10",
  expire: "border-red-500/30 text-red-400 bg-red-500/10",
  inactif: "border-zinc-700 text-zinc-500",
  non_souscrit: "border-zinc-700 text-zinc-500",
};

const LigneModule = ({ entId, cle, m, onDone }) => {
  const [statut, setStatut] = useState(m.statut === "non_souscrit" ? "actif" : m.statut);
  const [jours, setJours] = useState(30);
  const [prixNego, setPrixNego] = useState(m.prix_negocie_fcfa ?? "");
  const [notes, setNotes] = useState(m.notes || "");
  const mut = useMutation({
    mutationFn: (b) => put(`/entreprises/${entId}/modules`, b),
    onSuccess: (r) => { toast.success(`« ${m.libelle} » → ${r.statut}`); onDone(); },
    onError: (e) => toast.error(e.message),
  });
  const enregistrer = () => mut.mutate({ module: cle, statut, jours_essai: Number(jours || 30),
    prix_fcfa: prixNego === "" ? null : Number(prixNego), notes });
  return (
    <div data-testid={`module-ligne-${cle}`} className="panel p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Boxes className="h-4 w-4 text-primary" />
        <span className="font-semibold text-sm">{m.libelle}</span>
        <span className={cn("font-mono text-[10px] uppercase px-1.5 py-0.5 border rounded-sm", BADGE[m.statut] || BADGE.inactif)}>
          {m.statut.replace("_", " ")}
        </span>
        {m.socle && <span className="text-[10px] font-mono uppercase border border-primary/40 text-primary px-1 rounded-sm">socle</span>}
        <span className="text-[11px] text-muted-foreground font-mono ml-auto">
          catalogue {prix(m.prix_catalogue_fcfa)}
          {m.prix_negocie_fcfa != null && ` · négocié ${prix(m.prix_negocie_fcfa)}`}
          {m.expire_le && ` · essai jusqu'au ${m.expire_le.slice(0, 10)}`}
        </span>
      </div>
      <p className="text-[11px] text-muted-foreground">{m.description}</p>
      {m.socle ? (
        <p className="text-[11px] text-emerald-400 flex items-center gap-1"><Check className="h-3.5 w-3.5" />Toujours inclus — non désactivable.</p>
      ) : !m.disponible ? (
        <p className="text-[11px] text-amber-400">Module marqué « pas encore vendable » dans le catalogue.</p>
      ) : (<>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <label className="space-y-1"><span className="eyebrow">Statut</span>
            <select data-testid={`module-statut-${cle}`} className={S} value={statut} onChange={(e) => setStatut(e.target.value)}>
              <option value="actif">Actif (facturé)</option>
              <option value="essai">Essai gratuit</option>
              <option value="inactif">Désactivé</option>
            </select>
          </label>
          {statut === "essai" && (
            <label className="space-y-1"><span className="eyebrow">Durée d'essai (jours)</span>
              <input data-testid={`module-jours-${cle}`} type="number" className={cn(S, "font-mono")} value={jours} onChange={(e) => setJours(e.target.value)} />
            </label>
          )}
          <label className="space-y-1"><span className="eyebrow">Prix négocié (FCFA)</span>
            <input data-testid={`module-prix-${cle}`} type="number" placeholder={`défaut : ${prix(m.prix_catalogue_fcfa)}`}
              className={cn(S, "font-mono")} value={prixNego} onChange={(e) => setPrixNego(e.target.value)} />
          </label>
          <label className="space-y-1 md:col-span-2"><span className="eyebrow">Note commerciale</span>
            <input data-testid={`module-notes-${cle}`} className={S} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Remise, pilote, engagement…" />
          </label>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" className="h-7 text-[11px] font-semibold" disabled={mut.isPending}
            data-testid={`module-save-${cle}`} onClick={enregistrer}>
            {statut === "essai" ? <Timer className="h-3.5 w-3.5 mr-1" /> : <Power className="h-3.5 w-3.5 mr-1" />}
            Appliquer
          </Button>
          <span className="text-[10px] text-muted-foreground">Une désactivation conserve toutes les données du module.</span>
        </div>
      </>)}
    </div>
  );
};

/** Modules d'UNE entreprise : activation, essai, prix négocié, et total mensuel facturé. */
export const ModulesEntrepriseDialog = ({ entreprise, onClose }) => {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["ent-modules", entreprise?.id], enabled: !!entreprise,
    queryFn: () => get(`/entreprises/${entreprise.id}/modules`) });
  const refresh = () => { q.refetch(); qc.invalidateQueries({ queryKey: ["catalogue"] }); qc.invalidateQueries({ queryKey: ["facturation"] }); qc.invalidateQueries({ queryKey: ["features"] }); };
  const f = q.data?.facturation;
  return (
    <Dialog open={!!entreprise} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl rounded-sm max-h-[90vh] overflow-auto" data-testid="modules-dialog">
        <DialogHeader><DialogTitle className="text-base">Modules & abonnement — {entreprise?.nom}</DialogTitle></DialogHeader>
        {q.isLoading ? <LoadingState rows={3} /> : q.error ? <ErrorState error={q.error} onRetry={q.refetch} /> : (
          <div className="space-y-2">
            <div data-testid="facturation-resume" className="panel p-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] font-mono">
              <span>Forfait <b className="text-foreground">{f?.plan_label}</b></span>
              <span>Forfait facturé : {prix(f?.prix_forfait_fcfa)}{f?.prix_negocie && f?.prix_catalogue_forfait_fcfa != null ? ` (catalogue ${prix(f.prix_catalogue_forfait_fcfa)})` : ""}</span>
              <span>Options : {f?.options?.length ? f.options.map((o) => `${o.libelle} ${prix(o.prix_fcfa)}`).join(" · ") : "aucune"}</span>
              <span className="ml-auto text-primary font-semibold">Total mensuel : {prix(f?.total_mensuel_fcfa)}</span>
            </div>
            {Object.entries(q.data.modules).map(([cle, m]) => (
              <LigneModule key={cle} entId={entreprise.id} cle={cle} m={m} onDone={refresh} />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
